import { KeyRound, Loader2, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { verifyPayoutBatchTwoFA } from '@/api/admin';

/**
 * PayoutTwoFactorModal — admin-side 2FA confirmation dialog.
 *
 * Triggered after a NOWPayments mass payout batch is submitted and returns
 * status="awaiting_2fa". The admin reads the 6-digit code from their
 * authenticator app (or email) and submits it. On success the local batch
 * doc flips to status="verified" and the modal closes.
 *
 * Props:
 *   - open: boolean
 *   - batchId: string (local BatchPayout.batch_id)
 *   - nowpaymentsBatchId: string (informational only)
 *   - onClose(): closes without verifying
 *   - onVerified(response): called after a successful verify, parent should
 *     refresh the payouts list and toast its own success message
 */
const PayoutTwoFactorModal = ({ open, batchId, nowpaymentsBatchId, onClose, onVerified }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: batchId is an intentional trigger — clear the code when the modal is reopened for a different batch
  useEffect(() => {
    if (open) setCode('');
  }, [open, batchId]);

  if (!open) return null;

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length < 4) {
      toast.error('Enter the verification code from your authenticator');
      return;
    }
    setBusy(true);
    try {
      const r = await verifyPayoutBatchTwoFA(batchId, trimmed);
      toast.success('Payout batch verified — NOWPayments will dispatch funds');
      onVerified?.(r);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Verify failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payout-2fa-title"
      data-testid="payout-2fa-modal"
    >
      <button type="button" className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-label="Close" onClick={() => !busy && onClose?.()} />
      <form onSubmit={submit} className="sc-glass-strong relative rounded-2xl p-6 md:p-7 w-full max-w-md" style={{ borderColor: 'rgba(255,182,18,0.32)' }}>
        <div
          className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
                style={{
                  background: 'rgba(255,182,18,0.06)',
                  borderColor: 'rgba(255,182,18,0.32)',
                }}
                aria-hidden="true"
              >
                <ShieldCheck className="w-5 h-5 text-[#FFC20E]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">NOWPayments · 2FA Required</div>
                <h3 id="payout-2fa-title" className="font-display font-bold text-lg mt-0.5">
                  Verify Payout Batch
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              className="p-1 rounded-md text-[var(--sc-text-mute)] hover:text-white"
              aria-label="Close"
              data-testid="payout-2fa-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="text-[12px] text-[var(--sc-text-dim)] leading-relaxed mb-4">
            Enter the verification code from your authenticator app (or email inbox) for the NOWPayments account.
          </div>

          <div
            className="rounded-xl border p-3 mb-4 text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)]"
            style={{
              background: 'rgba(0,0,0,0.32)',
              borderColor: 'rgba(255,182,18,0.18)',
            }}
            data-testid="payout-2fa-batch-info"
          >
            <div>
              Local batch: <span className="text-white normal-case">{batchId}</span>
            </div>
            {nowpaymentsBatchId && (
              <div className="mt-1">
                NOWPayments id: <span className="text-white normal-case">{nowpaymentsBatchId}</span>
              </div>
            )}
          </div>

          <label htmlFor="payout-2fa-code" className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] flex items-center gap-1.5">
            <KeyRound className="w-3 h-3 text-[#FFC20E]" />
            Verification code
          </label>
          <input
            id="payout-2fa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            maxLength={12}
            className="mt-1 w-full rounded-xl border bg-[rgba(0,0,0,0.32)] border-[rgba(255,182,18,0.32)] px-3 py-2 font-display font-bold text-2xl tracking-[0.3em] text-center sc-tabular outline-none focus:border-[#FFC20E]"
            placeholder="••••••"
            data-testid="payout-2fa-input"
          />

          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              disabled={busy}
              className="px-3 py-2 rounded-xl border text-[12px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] disabled:opacity-50"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255,182,18,0.18)',
              }}
              data-testid="payout-2fa-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || code.trim().length < 4}
              className="px-4 py-2 rounded-xl font-bold text-[12px] uppercase tracking-[0.18em] text-black disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
                border: '1px solid rgba(255,182,18,0.85)',
              }}
              data-testid="payout-2fa-submit"
            >
              {busy ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                </span>
              ) : (
                'Verify & Dispatch'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PayoutTwoFactorModal;
